import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { ListingCard } from "@/components/site/ListingCard";
import {
  getLocation,
  listingsByLocation,
  categories,
  comboHref,
  locations,
  blogPosts,
  blogHref,
} from "@/lib/data";
import { ArrowRight, MapPin, Star, Users, Sparkles, Building2 } from "lucide-react";
import { generateBreadcrumbSchema, generateWebPageSchema } from "@/lib/utils";

const SITE_URL = "https://letslistbusiness.com";

export const Route = createFileRoute("/location/$slug/")({
  loader: ({ params }) => {
    const loc = getLocation(params.slug);
    if (!loc) throw notFound();
    return { loc };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const { loc } = loaderData;
    const title = `Best Local Businesses in ${loc.name}, ${loc.state} | LetsListBusiness`;
    const description = `Find top-rated restaurants, home services, and more in ${loc.name}. Verified reviews and local recommendations.`;
    const url = `${SITE_URL}/location/${loc.slug}/`;
    
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:image", content: loc.image },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
      schema: [
        generateWebPageSchema(title, description, url),
        generateBreadcrumbSchema([
          { label: "Home", href: "/" },
          { label: "Locations", href: "/location/" },
          { label: loc.name, href: url },
        ], SITE_URL),
      ],
    };
  },
  component: LocationPage,
  notFoundComponent: () => (
    <div className="container mx-auto px-4 py-20 text-center">
      <h1 className="font-display text-3xl font-bold">City not found</h1>
      <Link to="/location" className="mt-4 inline-block text-primary">Browse all cities</Link>
    </div>
  ),
});

function LocationPage() {
  const { loc } = Route.useLoaderData();
  const items = listingsByLocation(loc.slug);
  const relatedPosts = blogPosts.filter((p) => p.location === loc.slug).slice(0, 3);
  const otherLocs = locations.filter((l) => l.slug !== loc.slug).slice(0, 5);
  const avgRating = items.length
    ? (items.reduce((s, l) => s + l.rating, 0) / items.length).toFixed(1)
    : "—";

  return (
    <>
      <Breadcrumbs
        items={[
          { label: "Home", href: "/" },
          { label: "Locations", href: "/location/" },
          { label: loc.name },
        ]}
      />

      {/* Full-bleed hero */}
      <section className="relative -mt-2 h-[70vh] min-h-[500px] w-full overflow-hidden">
        <img src={loc.image} alt={`${loc.name}, ${loc.state}`} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-linear-to-b from-black/40 via-black/30 to-black/85" />
        <div className="container relative mx-auto flex h-full flex-col justify-end px-4 pb-16 text-white">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.3em] text-white/80">
            <MapPin className="h-3 w-3" /> {loc.state}
          </div>
          <h1 className="mt-3 font-display text-6xl font-bold leading-[0.95] sm:text-8xl">{loc.name}</h1>
          <p className="mt-5 max-w-2xl text-lg text-white/85">{loc.description}</p>
        </div>
      </section>

      {/* Stats strip */}
      <section className="border-b border-border bg-card">
        <div className="container mx-auto grid grid-cols-2 divide-x divide-border px-4 sm:grid-cols-4">
          {[
            { icon: Building2, label: "Listings", value: items.length },
            { icon: Star, label: "Avg. rating", value: avgRating },
            { icon: Users, label: "Population", value: loc.population },
            { icon: MapPin, label: "Categories", value: categories.length },
          ].map((s, i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-6 sm:px-6">
              <s.icon className="h-5 w-5 text-primary" />
              <div>
                <div className="font-display text-2xl font-bold">{s.value}</div>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{s.label}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Categories — pill cloud, big */}
      <section className="container mx-auto px-4 py-16">
        <div className="grid gap-6 lg:grid-cols-3">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">01 — Categories</div>
            <h2 className="mt-3 font-display text-3xl font-bold sm:text-4xl">Browse {loc.name} by what you need</h2>
          </div>
          <div className="lg:col-span-2">
            <div className="flex flex-wrap gap-2">
              {categories.map((c) => (
                <Link
                  key={c.slug}
                  {...comboHref(loc.slug, c.slug)}
                  className="group inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 font-medium transition-all hover:-translate-y-0.5 hover:border-primary hover:bg-primary hover:text-primary-foreground hover:shadow-card"
                >
                  <span className="text-lg">{c.icon}</span>
                  <span>{c.name}</span>
                  <span className="text-xs opacity-60 group-hover:opacity-100">in {loc.name}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Listings */}
      <section className="bg-surface py-16">
        <div className="container mx-auto px-4">
          <div className="grid gap-6 lg:grid-cols-3">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">02 — Top picks</div>
              <h2 className="mt-3 font-display text-3xl font-bold sm:text-4xl">What's worth your time in {loc.name}</h2>
            </div>
            <p className="text-muted-foreground lg:col-span-2 lg:pt-12">
              The highest-rated, most-reviewed and most-recommended businesses
              across the city. Updated continuously by neighbors.
            </p>
          </div>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {items.slice(0, 12).map((l) => (
              <ListingCard key={l.id} listing={l} />
            ))}
          </div>
        </div>
      </section>

      {/* Local guides */}
      {relatedPosts.length > 0 && (
        <section className="container mx-auto px-4 py-16">
          <div className="grid gap-6 lg:grid-cols-3">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">03 — Read up</div>
              <h2 className="mt-3 font-display text-3xl font-bold sm:text-4xl">Local guides for {loc.name}</h2>
            </div>
            <div className="grid gap-5 sm:grid-cols-2 lg:col-span-2 lg:grid-cols-3">
              {relatedPosts.map((p) => (
                <Link key={p.slug} {...blogHref(p.slug)} className="group block overflow-hidden rounded-2xl border border-border bg-card shadow-card hover:-translate-y-0.5 hover:shadow-card-lg transition-all">
                  <img src={p.image} alt={p.title} loading="lazy" className="aspect-16/10 w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="p-4">
                    <h3 className="font-display text-base font-semibold leading-tight">{p.title}</h3>
                    <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{p.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Other cities */}
      <section className="border-t border-border bg-card">
        <div className="container mx-auto px-4 py-12">
          <div className="flex flex-wrap items-baseline justify-between gap-4">
            <h2 className="font-display text-2xl font-bold">Explore other cities</h2>
            <Link to="/location" className="text-sm font-medium text-primary hover:underline">All cities →</Link>
          </div>
          <div className="mt-5 flex flex-wrap gap-2">
            {otherLocs.map((l) => (
              <Link key={l.slug} to="/location/$slug" params={{ slug: l.slug }} className="rounded-full border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-muted">
                {l.name}, {l.state}
              </Link>
            ))}
          </div>
        </div>
      </section>
      {/* City Context — expansion */}
      <section className="border-y border-border bg-card py-24">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-4xl text-center">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Local Insights</div>
            <h2 className="mt-4 font-display text-4xl font-bold">Why {loc.name} is a hub for independent business.</h2>
            <p className="mt-6 text-xl leading-relaxed text-muted-foreground">
              {loc.name} isn't just a place on a map; it's a living ecosystem of creators, entrepreneurs, and dedicated service providers. The city's unique blend of {loc.state} heritage and modern innovation creates the perfect environment for small businesses to thrive and offer something truly different from national chains.
            </p>
            <div className="mt-16 grid gap-8 md:grid-cols-3">
              {[
                { label: "Community Support", value: "High", desc: "Residents prioritize local over global." },
                { label: "Business Growth", value: "Steady", desc: "New ventures opening every month." },
                { label: "Local Flavor", value: "Unique", desc: "Offerings you won't find elsewhere." },
              ].map((item) => (
                <div key={item.label} className="rounded-2xl bg-surface border border-border p-6 shadow-sm">
                  <div className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{item.label}</div>
                  <div className="mt-2 font-display text-3xl font-bold text-primary">{item.value}</div>
                  <p className="mt-2 text-xs text-muted-foreground">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Article — Internal Linking */}
      <section className="container mx-auto px-4 py-24">
        <div className="rounded-[3rem] border border-border bg-card p-1 items-center grid lg:grid-cols-2">
          <div className="p-8 sm:p-12 lg:p-16">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">City Spotlight</div>
            <h2 className="mt-6 font-display text-4xl font-bold leading-tight">Everything you need to know about life in {loc.name}.</h2>
            <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
              Whether you're a long-time resident or just moved to {loc.name}, our comprehensive local guide covers everything from the best hidden parks to the most reliable home service providers in the {loc.state} region.
            </p>
            <Link to="/blog" className="mt-8 inline-flex items-center justify-center rounded-full bg-foreground px-8 py-4 text-sm font-bold text-background transition-transform hover:scale-105">
              View local guides
            </Link>
          </div>
          <div className="hidden lg:block h-full">
            <img
              src="https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?w=1200&q=80"
              alt={`${loc.name} cityscape`}
              className="h-full w-full object-cover rounded-[2.8rem]"
            />
          </div>
        </div>
      </section>
    </>
  );
}
